// Smart Daara - Parent Dashboard
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { progressAPI, userAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { 
  Users, BookOpen, CheckCircle, Clock, Eye, Bell, 
  TrendingUp, ArrowRight, GraduationCap
} from 'lucide-react';

export default function ParentDashboard() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [childProgress, setChildProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadParentData();
  }, [user]);

  const loadParentData = async () => {
    try {
      const childrenResponse = await userAPI.getMyChildren();
      const studentsList = childrenResponse.data || [];
      setChildren(studentsList);

      if (studentsList.length > 0) {
        setSelectedChild(studentsList[0]);
        await loadChildProgress(studentsList[0].id);
      }
    } catch (error) {
      console.error('Error loading parent data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadChildProgress = async (studentId) => {
    try {
      const progressResponse = await progressAPI.getChildProgress(studentId);
      setChildProgress(progressResponse.data);
    } catch (error) {
      console.error('Error loading child progress:', error);
    }
  };

  const handleChildSelect = async (child) => {
    setSelectedChild(child);
    await loadChildProgress(child.id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parent-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-slate-900">
            {t('welcome')}, {user?.first_name}!
          </h1>
          <p className="text-slate-500">
            {language === 'fr' 
              ? 'Suivez la progression de vos enfants'
              : "Track your children's progress"}
          </p>
        </div>
        <Button variant="outline">
          <Bell className="w-4 h-4 mr-2" />
          {t('notifications')}
        </Button>
      </div>

      {/* Children Selector */}
      {children.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-outfit">
              {language === 'fr' ? 'Vos Enfants' : 'Your Children'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {children.map((child) => (
                <button
                  key={child.id}
                  onClick={() => handleChildSelect(child)}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                    selectedChild?.id === child.id
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-slate-200 hover:border-emerald-300'
                  }`}
                  data-testid={`child-selector-${child.id}`}
                >
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={child.avatar_url} />
                    <AvatarFallback className="bg-emerald-100 text-emerald-700">
                      {child.first_name?.[0]}{child.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left">
                    <p className="font-medium text-slate-900">
                      {child.first_name} {child.last_name}
                    </p>
                    <p className="text-xs text-slate-500">
                      {language === 'fr' ? 'Étudiant' : 'Student'}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Selected Child Progress */}
      {selectedChild && childProgress && (
        <>
          {/* Progress Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <BookOpen className="w-5 h-5 text-emerald-700" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-slate-900">
                      {childProgress.courses?.length || 0}
                    </p>
                    <p className="text-xs text-slate-500">
                      {language === 'fr' ? 'Cours Inscrits' : 'Enrolled Courses'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-amber-700" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-slate-900">
                      {childProgress.courses?.reduce((sum, c) => sum + (c.lessons_completed || 0), 0) || 0}
                    </p>
                    <p className="text-xs text-slate-500">
                      {language === 'fr' ? 'Leçons Terminées' : 'Lessons Completed'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-blue-700" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-slate-900">
                      {childProgress.courses?.length > 0
                        ? Math.round(
                            childProgress.courses.reduce((sum, c) => sum + (c.progress_percentage || 0), 0) /
                            childProgress.courses.length
                          )
                        : 0}%
                    </p>
                    <p className="text-xs text-slate-500">
                      {language === 'fr' ? 'Progression Moyenne' : 'Average Progress'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Clock className="w-5 h-5 text-purple-700" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-slate-900">12h</p>
                    <p className="text-xs text-slate-500">
                      {language === 'fr' ? 'Temps d\'étude' : 'Study Time'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Course Progress Details */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-outfit">
                {language === 'fr' 
                  ? `Progression de ${selectedChild.first_name}`
                  : `${selectedChild.first_name}'s Progress`}
              </CardTitle>
              <CardDescription>
                {language === 'fr'
                  ? 'Détail par cours'
                  : 'Progress by course'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {childProgress.courses && childProgress.courses.length > 0 ? (
                <div className="space-y-4">
                  {childProgress.courses.map((course, index) => (
                    <div 
                      key={index}
                      className="p-4 border border-slate-200 rounded-lg"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <BookOpen className="w-5 h-5 text-emerald-700" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {course.course_title_fr || course.course_title}
                            </p>
                            <p className="text-sm text-slate-500">
                              {course.lessons_completed || 0}/{course.total_lessons || 0} {t('lessons')}
                            </p>
                          </div>
                        </div>
                        {course.progress_percentage >= 100 && (
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            {t('completed')}
                          </span>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">{t('progress')}</span>
                          <span className="font-medium text-emerald-700">
                            {Math.round(course.progress_percentage || 0)}%
                          </span>
                        </div>
                        <Progress value={course.progress_percentage || 0} className="h-2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <GraduationCap className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500">
                    {language === 'fr'
                      ? 'Aucun cours en cours'
                      : 'No courses in progress'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* No Children State */}
      {children.length === 0 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-8 text-center">
            <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="font-medium text-slate-900 mb-2">
              {language === 'fr'
                ? 'Aucun enfant associé'
                : 'No children associated'}
            </h3>
            <p className="text-slate-500">
              {language === 'fr'
                ? 'Contactez l\'administrateur de l\'école pour associer vos enfants à votre compte.'
                : 'Contact your school administrator to associate your children with your account.'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
